import { getLocalStorage } from "./api";
import {
  IconFileText,
  IconGauge,
  IconFileUpload,
  IconSquareCheck,
  IconLink,
  IconUser,
  IconFunction,
  IconKey,
  IconListCheck,
  IconSubtask,
  IconRobot,
  IconRocket,
  IconProps,
  Icon,
  IconLockCode,
  IconHeartRateMonitor,
  IconPolygon,
} from "@tabler/icons-react";

import { customAlphabet } from "nanoid";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { AccessOptionsType } from "./types";

// Capital letters not used as postgres functions are not case sensitive. This is used to generate function names
const nanoid = customAlphabet("1234567890abcdefghijklmnopqrstuvwxyz", 10);

export const getId = (length = 5) => {
  return nanoid(length);
};

export const escapeRegExp = (
  str: string, // or better use 'escape-string-regexp' package
) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const sortOrder = [
  "Dashboard",
  "Tasks",
  "Datasets",
  "Relationships",
  "Reconsets",
  "Use Cases",
  "Loaders",
  "Adapters",
  "Functions",
  "Users",
  "Roles",
];

export function getRoleAccessOptions(
  entityId: string,
  userRoles: {
    entityId: string;
    accessType: string;
    accessOption: string;
    roleType: string;
  }[],
) {
  const entityRoles = (userRoles || []).filter((role) => role.entityId === entityId);

  const options: AccessOptionsType = [];

  // console.log("entityRole", entityRole);

  options.push({
    label: "Dashboard",
    icon: IconGauge,
    link: `/app/dashboard?entityId=${entityId}`,
    order: 1,
  });

  options.push({
    label: "Tasks",
    icon: IconSubtask,
    link: `/app/tasks?entityId=${entityId}`,
    order: 2,
  });

  for (const entityRole of entityRoles) {
    if (entityRole.accessType === "DATASET" && entityRole.accessOption === "VIEW") {
      const option = options.find((option) => option.label.toLowerCase() === "data model");

      if (option) {
        if ("link" in option) {
          // Main menu. No sub menus
        } else {
          const subMenu = (option.subMenus || []).find((option) => option.label.toLowerCase() === "dataset");
          if (option.subMenus && Array.isArray(option.subMenus)) {
            option.subMenus.push({
              label: "Datasets",
              icon: IconFileText,
              link: `/app/datasets?entityId=${entityId}`,
              order: 1,
            });
          } else {
            option.subMenus = [
              {
                label: "Datasets",
                icon: IconFileText,
                link: `/app/datasets?entityId=${entityId}`,
                order: 1,
              },
            ];
          }
        }
      } else {
        options.push({
          label: "Data Model",
          icon: IconFileText,
          order: 3,
          subMenus: [
            {
              label: "Datasets",
              icon: IconFileText,
              link: `/app/datasets?entityId=${entityId}`,
              order: 1,
            },
          ],
        });
      }
    } else if (entityRole.accessType === "RELATIONSHIP" && entityRole.accessOption === "VIEW") {
      const option = options.find((option) => option.label.toLowerCase() === "data model");

      if (option) {
        if ("link" in option) {
          // Main menu. No sub menus
        } else {
          const subMenu = (option.subMenus || []).find((option) => option.label.toLowerCase() === "relationship");
          if (option.subMenus && Array.isArray(option.subMenus)) {
            option.subMenus.push({
              label: "Relationships",
              icon: IconLink,
              link: `/app/relationships?entityId=${entityId}`,
              order: 2,
            });
          } else {
            option.subMenus = [
              {
                label: "Relationships",
                icon: IconLink,
                link: `/app/relationships?entityId=${entityId}`,
                order: 2,
              },
            ];
          }
        }
      } else {
        options.push({
          label: "Data Model",
          icon: IconFileText,
          order: 3,
          subMenus: [
            {
              label: "Relationships",
              icon: IconLink,
              link: `/app/relationships?entityId=${entityId}`,
              order: 2,
            },
          ],
        });
      }
    } else if (entityRole.accessType === "LOADER" && entityRole.accessOption === "VIEW") {
      const option = options.find((option) => option.label.toLowerCase() === "adapter");

      if (option) {
        if ("link" in option) {
          // Main menu. No sub menus
        } else {
          if (option.subMenus && Array.isArray(option.subMenus)) {
            option.subMenus.push({
              label: "Loaders",
              icon: IconFileUpload,
              link: `/app/loaders?entityId=${entityId}`,
              order: 1,
            });
          } else {
            option.subMenus = [
              {
                label: "Loaders",
                icon: IconFileUpload,
                link: `/app/loaders?entityId=${entityId}`,
                order: 1,
              },
            ];
          }
        }
      } else {
        options.push({
          label: "Adapters",
          icon: IconRobot,
          order: 40,
          subMenus: [
            {
              label: "Loaders",
              icon: IconFileUpload,
              link: `/app/loaders?entityId=${entityId}`,
              order: 1,
            },
          ],
        });
      }
    } else if (entityRole.accessType === "FUNCTION" && entityRole.accessOption === "VIEW") {
      options.push({
        label: "Functions",
        icon: IconFunction,
        link: `/app/functions?entityId=${entityId}`,
        order: 50,
      });
    } else if (entityRole.accessType === "USER" && entityRole.accessOption === "VIEW") {
      const option = options.find((option) => option.label.toLowerCase() === "access control");

      if (option) {
        if ("link" in option) {
          // Main menu. No sub menus
        } else {
          if (option.subMenus && Array.isArray(option.subMenus)) {
            option.subMenus.push({
              label: "Users",
              icon: IconUser,
              link: `/app/users?entityId=${entityId}`,
              order: 1,
            });
          } else {
            option.subMenus = [
              {
                label: "Users",
                icon: IconUser,
                link: `/app/users?entityId=${entityId}`,
                order: 1,
              },
            ];
          }
        }
      } else {
        options.push({
          label: "Access Control",
          icon: IconKey,
          order: 60,
          subMenus: [
            {
              label: "Users",
              icon: IconUser,
              link: `/app/users?entityId=${entityId}`,
              order: 1,
            },
          ],
        });
      }
    } else if (entityRole.accessType === "POLICY" && entityRole.accessOption === "VIEW") {
      const option = options.find((option) => option.label.toLowerCase() === "access control");

      if (option) {
        if ("link" in option) {
          // Main menu. No sub menus
        } else {
          if (option.subMenus && Array.isArray(option.subMenus)) {
            option.subMenus.push({
              label: "Policies",
              icon: IconPolygon,
              link: `/app/policies?entityId=${entityId}`,
              order: 3,
            });
          } else {
            option.subMenus = [
              {
                label: "Policies",
                icon: IconPolygon,
                link: `/app/policies?entityId=${entityId}`,
                order: 3,
              },
            ];
          }
        }
      } else {
        options.push({
          label: "Access Control",
          icon: IconKey,
          order: 60,
          subMenus: [
            {
              label: "Policies",
              icon: IconPolygon,
              link: `/app/policies?entityId=${entityId}`,
              order: 3,
            },
          ],
        });
      }
    } else if (entityRole.accessType === "ROLE" && entityRole.accessOption === "VIEW") {
      const option = options.find((option) => option.label.toLowerCase() === "access control");

      if (option) {
        if ("link" in option) {
          // Main menu. No sub menus
        } else {
          if (option.subMenus && Array.isArray(option.subMenus)) {
            option.subMenus.push({
              label: "Roles",
              icon: IconLockCode,
              link: `/app/roles?entityId=${entityId}`,
              order: 2,
            });
          } else {
            option.subMenus = [
              {
                label: "Roles",
                icon: IconLockCode,
                link: `/app/roles?entityId=${entityId}`,
                order: 2,
              },
            ];
          }
        }
      } else {
        options.push({
          label: "Access Control",
          icon: IconKey,
          order: 60,
          subMenus: [
            {
              label: "Roles",
              icon: IconLockCode,
              link: `/app/roles?entityId=${entityId}`,
              order: 2,
            },
          ],
        });
      }
    } else if (entityRole.accessType === "USECASE" && entityRole.accessOption === "VIEW") {
      const option = options.find((option) => option.label.toLowerCase() === "usecases");

      if (option) {
        if ("link" in option) {
          // Main menu. No sub menus
        } else {
          if (option.subMenus && Array.isArray(option.subMenus)) {
            option.subMenus.push({
              label: "Monitoring",
              icon: IconHeartRateMonitor,
              link: `/app/usecases?entityId=${entityId}`,
              order: 1,
            });
          } else {
            option.subMenus = [
              {
                label: "Monitoring",
                icon: IconHeartRateMonitor,
                link: `/app/usecases?entityId=${entityId}`,
                order: 1,
              },
            ];
          }
        }
      } else {
        options.push({
          label: "Usecases",
          icon: IconSquareCheck,
          order: 20,
          subMenus: [
            {
              label: "Monitoring",
              icon: IconHeartRateMonitor,
              link: `/app/usecases?entityId=${entityId}`,
              order: 1,
            },
          ],
        });
      }
    } else if (entityRole.accessType === "RECONSET" && entityRole.accessOption === "VIEW") {
      const option = options.find((option) => option.label.toLowerCase() === "usecases");

      if (option) {
        if ("link" in option) {
          // Main menu. No sub menus
        } else {
          if (option.subMenus && Array.isArray(option.subMenus)) {
            option.subMenus.push({
              label: "Reconciliation",
              icon: IconListCheck,
              link: `/app/reconsets?entityId=${entityId}`,
              order: 1,
            });
          } else {
            option.subMenus = [
              {
                label: "Reconciliation",
                icon: IconListCheck,
                link: `/app/reconsets?entityId=${entityId}`,
                order: 1,
              },
            ];
          }
        }
      } else {
        options.push({
          label: "Usecases",
          icon: IconSquareCheck,
          order: 20,
          subMenus: [
            {
              label: "Reconciliation",
              icon: IconListCheck,
              link: `/app/reconsets?entityId=${entityId}`,
              order: 1,
            },
          ],
        });
      }
    }
  }

  return options;
}

export function getDashboardActions(entityId: string, allowedActions: Record<string, boolean> | undefined) {
  const options: AccessOptionsType = [];

  options.push({
    label: "Dashboard",
    icon: IconGauge,
    link: `/app/dashboard?entityId=${entityId}`,
    order: 1,
  });

  options.push({
    label: "Tasks",
    icon: IconSubtask,
    link: `/app/tasks?entityId=${entityId}`,
    order: 2,
  });

  if (!allowedActions) {
    return options;
  }

  for (const entityRole of Object.keys(allowedActions)) {
    if (entityRole.toLowerCase() === "dataset:definition:view" && allowedActions[entityRole]) {
      const option = options.find((option) => option.label.toLowerCase() === "data model");

      if (option) {
        if ("link" in option) {
          // Main menu. No sub menus
        } else {
          const subMenu = (option.subMenus || []).find((option) => option.label.toLowerCase() === "dataset");
          if (option.subMenus && Array.isArray(option.subMenus)) {
            option.subMenus.push({
              label: "Datasets",
              icon: IconFileText,
              link: `/app/datasets?entityId=${entityId}`,
              order: 1,
            });
          } else {
            option.subMenus = [
              {
                label: "Datasets",
                icon: IconFileText,
                link: `/app/datasets?entityId=${entityId}`,
                order: 1,
              },
            ];
          }
        }
      } else {
        options.push({
          label: "Data Model",
          icon: IconFileText,
          order: 3,
          subMenus: [
            {
              label: "Datasets",
              icon: IconFileText,
              link: `/app/datasets?entityId=${entityId}`,
              order: 1,
            },
          ],
        });
      }
    } else if (entityRole.toLowerCase() === "relationship:definition:view" && allowedActions[entityRole]) {
      const option = options.find((option) => option.label.toLowerCase() === "data model");

      if (option) {
        if ("link" in option) {
          // Main menu. No sub menus
        } else {
          const subMenu = (option.subMenus || []).find((option) => option.label.toLowerCase() === "relationship");
          if (option.subMenus && Array.isArray(option.subMenus)) {
            option.subMenus.push({
              label: "Relationships",
              icon: IconLink,
              link: `/app/relationships?entityId=${entityId}`,
              order: 2,
            });
          } else {
            option.subMenus = [
              {
                label: "Relationships",
                icon: IconLink,
                link: `/app/relationships?entityId=${entityId}`,
                order: 2,
              },
            ];
          }
        }
      } else {
        options.push({
          label: "Data Model",
          icon: IconFileText,
          order: 3,
          subMenus: [
            {
              label: "Relationships",
              icon: IconLink,
              link: `/app/relationships?entityId=${entityId}`,
              order: 2,
            },
          ],
        });
      }
    } else if (entityRole.toLowerCase() === "loader:definition:view" && allowedActions[entityRole]) {
      const option = options.find((option) => option.label.toLowerCase() === "adapter");

      if (option) {
        if ("link" in option) {
          // Main menu. No sub menus
        } else {
          if (option.subMenus && Array.isArray(option.subMenus)) {
            option.subMenus.push({
              label: "Loaders",
              icon: IconFileUpload,
              link: `/app/loaders?entityId=${entityId}`,
              order: 1,
            });
          } else {
            option.subMenus = [
              {
                label: "Loaders",
                icon: IconFileUpload,
                link: `/app/loaders?entityId=${entityId}`,
                order: 1,
              },
            ];
          }
        }
      } else {
        options.push({
          label: "Adapters",
          icon: IconRobot,
          order: 40,
          subMenus: [
            {
              label: "Loaders",
              icon: IconFileUpload,
              link: `/app/loaders?entityId=${entityId}`,
              order: 1,
            },
          ],
        });
      }
    } else if (entityRole.toLowerCase() === "function:definition:view" && allowedActions[entityRole]) {
      options.push({
        label: "Functions",
        icon: IconFunction,
        link: `/app/functions?entityId=${entityId}`,
        order: 50,
      });
    } else if (entityRole.toLowerCase() === "user:definition:view" && allowedActions[entityRole]) {
      const option = options.find((option) => option.label.toLowerCase() === "access control");

      if (option) {
        if ("link" in option) {
          // Main menu. No sub menus
        } else {
          if (option.subMenus && Array.isArray(option.subMenus)) {
            option.subMenus.push({
              label: "Users",
              icon: IconUser,
              link: `/app/users?entityId=${entityId}`,
              order: 1,
            });
          } else {
            option.subMenus = [
              {
                label: "Users",
                icon: IconUser,
                link: `/app/users?entityId=${entityId}`,
                order: 1,
              },
            ];
          }
        }
      } else {
        options.push({
          label: "Access Control",
          icon: IconKey,
          order: 60,
          subMenus: [
            {
              label: "Users",
              icon: IconUser,
              link: `/app/users?entityId=${entityId}`,
              order: 1,
            },
          ],
        });
      }
    } else if (entityRole.toLowerCase() === "policy:definition:view" && allowedActions[entityRole]) {
      const option = options.find((option) => option.label.toLowerCase() === "access control");

      if (option) {
        if ("link" in option) {
          // Main menu. No sub menus
        } else {
          if (option.subMenus && Array.isArray(option.subMenus)) {
            option.subMenus.push({
              label: "Policies",
              icon: IconPolygon,
              link: `/app/policies?entityId=${entityId}`,
              order: 3,
            });
          } else {
            option.subMenus = [
              {
                label: "Policies",
                icon: IconPolygon,
                link: `/app/policies?entityId=${entityId}`,
                order: 3,
              },
            ];
          }
        }
      } else {
        options.push({
          label: "Access Control",
          icon: IconKey,
          order: 60,
          subMenus: [
            {
              label: "Policies",
              icon: IconPolygon,
              link: `/app/policies?entityId=${entityId}`,
              order: 3,
            },
          ],
        });
      }
    } else if (entityRole.toLowerCase() === "role:definition:view" && allowedActions[entityRole]) {
      const option = options.find((option) => option.label.toLowerCase() === "access control");

      if (option) {
        if ("link" in option) {
          // Main menu. No sub menus
        } else {
          if (option.subMenus && Array.isArray(option.subMenus)) {
            option.subMenus.push({
              label: "Roles",
              icon: IconLockCode,
              link: `/app/roles?entityId=${entityId}`,
              order: 2,
            });
          } else {
            option.subMenus = [
              {
                label: "Roles",
                icon: IconLockCode,
                link: `/app/roles?entityId=${entityId}`,
                order: 2,
              },
            ];
          }
        }
      } else {
        options.push({
          label: "Access Control",
          icon: IconKey,
          order: 60,
          subMenus: [
            {
              label: "Roles",
              icon: IconLockCode,
              link: `/app/roles?entityId=${entityId}`,
              order: 2,
            },
          ],
        });
      }
    } else if (entityRole.toLowerCase() === "usecase:definition:view" && allowedActions[entityRole]) {
      const option = options.find((option) => option.label.toLowerCase() === "usecases");

      if (option) {
        if ("link" in option) {
          // Main menu. No sub menus
        } else {
          if (option.subMenus && Array.isArray(option.subMenus)) {
            option.subMenus.push({
              label: "Monitoring",
              icon: IconHeartRateMonitor,
              link: `/app/usecases?entityId=${entityId}`,
              order: 1,
            });
          } else {
            option.subMenus = [
              {
                label: "Monitoring",
                icon: IconHeartRateMonitor,
                link: `/app/usecases?entityId=${entityId}`,
                order: 1,
              },
            ];
          }
        }
      } else {
        options.push({
          label: "Usecases",
          icon: IconSquareCheck,
          order: 20,
          subMenus: [
            {
              label: "Monitoring",
              icon: IconHeartRateMonitor,
              link: `/app/usecases?entityId=${entityId}`,
              order: 1,
            },
          ],
        });
      }
    } else if (entityRole.toLowerCase() === "reconset:definition:view" && allowedActions[entityRole]) {
      const option = options.find((option) => option.label.toLowerCase() === "usecases");

      if (option) {
        if ("link" in option) {
          // Main menu. No sub menus
        } else {
          if (option.subMenus && Array.isArray(option.subMenus)) {
            option.subMenus.push({
              label: "Reconciliation",
              icon: IconListCheck,
              link: `/app/reconsets?entityId=${entityId}`,
              order: 1,
            });
          } else {
            option.subMenus = [
              {
                label: "Reconciliation",
                icon: IconListCheck,
                link: `/app/reconsets?entityId=${entityId}`,
                order: 1,
              },
            ];
          }
        }
      } else {
        options.push({
          label: "Usecases",
          icon: IconSquareCheck,
          order: 20,
          subMenus: [
            {
              label: "Reconciliation",
              icon: IconListCheck,
              link: `/app/reconsets?entityId=${entityId}`,
              order: 1,
            },
          ],
        });
      }
    } else if (entityRole.toLowerCase() === "queryset:definition:view" && allowedActions[entityRole]) {
      const option = options.find((option) => option.label.toLowerCase() === "usecases");

      if (option) {
        if ("link" in option) {
          // Main menu. No sub menus
        } else {
          if (option.subMenus && Array.isArray(option.subMenus)) {
            option.subMenus.push({
              label: "Querysets",
              icon: IconListCheck,
              link: `/app/querysets?entityId=${entityId}`,
              order: 1,
            });
          } else {
            option.subMenus = [
              {
                label: "Querysets",
                icon: IconListCheck,
                link: `/app/querysets?entityId=${entityId}`,
                order: 1,
              },
            ];
          }
        }
      } else {
        options.push({
          label: "Usecases",
          icon: IconSquareCheck,
          order: 20,
          subMenus: [
            {
              label: "Querysets",
              icon: IconListCheck,
              link: `/app/querysets?entityId=${entityId}`,
              order: 1,
            },
          ],
        });
      }
    }
  }

  return options;
}

export function showError(error: AxiosError<{ message: string }>, title: string, message: string, icon: any) {
  showNotification({
    id: "error",
    title: title,
    message: error?.response?.data?.message ? error?.response?.data?.message : message ? "Unknown error" : "Error",
    color: "red",
    icon: icon,
  });
}

export function hasEntityAdminTypeRole(
  entityId: string,
  userRoles: {
    entityId: string;
    accessType: string;
    accessOption: string;
    roleType: string;
  }[],
) {
  const entityRoles = (userRoles || []).filter((role) => role.entityId === entityId && role.roleType === "ADMIN");

  return entityRoles.length > 0;
}

export function hasEntityRoleAttached(
  entityId: string,
  userRoles: {
    entityId: string;
    accessType: string;
    accessOption: string;
    roleType: string;
    role: string;
  }[],
  roles: string[],
) {
  const entityRoles = (userRoles || []).filter(
    (userRole) => userRole.entityId === entityId && roles.indexOf(userRole.role) !== -1,
  );

  return entityRoles.length > 0;
}

export function getIsMatch(filter: string[]) {
  const isMatch = (data: any) => {
    let found = filter.every((search) => {
      const re = new RegExp(escapeRegExp(search), "i");
      let item = Object.keys(data).find((column) => {
        return re.test(data[column]);
      });
      if (item) {
        return true;
      }
      return false;
    });
    return found;
  };
  return isMatch;
}

export function isAuthorized() {
  return getLocalStorage("token") ? true : false;
}

export function getErrorMessage(error: any, defaultMessage: string = "") {
  let response = "";
  if (error.code && error.code?.substring(0, 2) === "AP") {
    response = `${defaultMessage ? `${defaultMessage} - ` : ""}${error.detail}`;
  } else if (error.response) {
    response = `${defaultMessage ? `${defaultMessage} - ` : ""}${error.response?.data?.message}`;
  } else if (error.message) {
    response = `${defaultMessage ? `${defaultMessage} - ` : ""}${error.message}`;
  } else {
    response = `${defaultMessage ? `${defaultMessage} - ` : ""}${error.output?.payload?.message}`;
  }

  return response;
}

export function getIsStringMatch(filter: string) {
  const isMatch = (option: { label: string; name: string }) => {
    const re = new RegExp(escapeRegExp(filter), "i");
    return re.test(option.name) || re.test(option.label);
  };
  return isMatch;
}

/**
 * Checks whether the user is allowed to perform the action
 * Resource access validation should be done separately
 *
 * @remarks
 *
 * @param action - The action/actions that is to be validated
 * @param allowedActions - The allowedActions response from the api /api/user/actions
 * @returns True or False - Based on whether the action is allowed
 *
 */
export function isActionAllowed(
  action: string | string[],
  allowedActions: Record<string, boolean> | undefined,
): boolean {
  // Build set of principals for the user
  if (!allowedActions) {
    return false;
  }

  if (action) {
    if (Array.isArray(action)) {
      for (const a of action) {
        if (allowedActions[a]) {
          return allowedActions[a];
        }
      }
    } else {
      if (allowedActions[action]) {
        return allowedActions[action];
      }
    }
  }

  return false;
}
